import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { db } from "@/lib/db-client";
import { SignaturePad } from "@/components/SignaturePad";
import { toast } from "sonner";
import { Plus, Trash2, LogOut, Shield } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { audit } from "@/lib/audit-client";
import logo from "@/assets/logo.png";

export const Route = createFileRoute("/_authenticated/")({
  component: DriverForm,
});

interface MaterialLine {
  material_id: string;
  tonnage: string;
}

function nowLocalISO() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

function DriverForm() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [type, setType] = useState<"depot" | "reprise">("depot");
  const [occurredAt, setOccurredAt] = useState(nowLocalISO());
  const [siteName, setSiteName] = useState("");
  const [clientId, setClientId] = useState("");
  const [truckPlate, setTruckPlate] = useState("");
  const [signerName, setSignerName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [signature, setSignature] = useState("");
  const [lines, setLines] = useState<MaterialLine[]>([{ material_id: "", tonnage: "" }]);
  const [submitting, setSubmitting] = useState(false);

  const [clients, setClients] = useState<Array<{ id: string; name: string; billing_email: string }>>([]);
  const [materials, setMaterials] = useState<Array<{ id: string; name: string }>>([]);
  const [maxMaterials, setMaxMaterials] = useState(3);

  // Redirect admin users to admin panel
  useEffect(() => {
    if (isAdmin) {
      navigate({ to: "/admin" });
    }
  }, [isAdmin, navigate]);

  useEffect(() => {
    (async () => {
      const [c, m, s] = await Promise.all([
        db.from("clients").select("id,name,billing_email").eq("active", true).order("name"),
        db.from("materials").select("id,name").eq("active", true).order("name"),
        db.from("settings").select("max_materials_per_truck").eq("id", 1).single(),
      ]);
      if (c.data) setClients(c.data);
      if (m.data) setMaterials(m.data);
      if (s.data) setMaxMaterials(s.data.max_materials_per_truck);
    })();
  }, []);

  const selectedClient = useMemo(() => clients.find(c => c.id === clientId), [clients, clientId]);
  useEffect(() => {
    if (selectedClient && !recipientEmail) setRecipientEmail(selectedClient.billing_email);
  }, [selectedClient]); // eslint-disable-line

  const addLine = () => {
    if (lines.length >= maxMaterials) {
      toast.warning(`Maximum ${maxMaterials} matières par camion`);
      return;
    }
    setLines([...lines, { material_id: "", tonnage: "" }]);
  };
  const removeLine = (i: number) => setLines(lines.filter((_, idx) => idx !== i));
  const updateLine = (i: number, patch: Partial<MaterialLine>) =>
    setLines(lines.map((l, idx) => idx === i ? { ...l, ...patch } : l));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientId) return toast.error("Sélectionnez un client");
    if (!signature) return toast.error("La signature est obligatoire");
    const validLines = lines.filter(l => l.material_id && parseFloat(l.tonnage) > 0);
    if (validLines.length === 0) return toast.error("Ajoutez au moins une matière avec un tonnage");
    if (new Set(validLines.map(l => l.material_id)).size !== validLines.length)
      return toast.error("Une matière ne peut apparaître qu'une fois");

    setSubmitting(true);
    try {
      const { data: ticket, error } = await db
        .from("weigh_tickets")
        .insert({
          type,
          occurred_at: new Date(occurredAt).toISOString(),
          site_name: siteName,
          client_id: clientId,
          truck_plate: truckPlate.toUpperCase(),
          driver_signature_name: signerName,
          signature_data_url: signature,
          recipient_email: recipientEmail,
        })
        .single();
      if (error) throw error;

      const { error: matErr } = await db
        .from("ticket_materials")
        .insert(validLines.map(l => ({
          ticket_id: ticket.id,
          material_id: l.material_id,
          tonnage: parseFloat(l.tonnage),
        })));
      if (matErr) throw matErr;

      await audit("ticket.create", {
        entity: "weigh_tickets",
        entity_id: ticket.id,
        metadata: {
          type, occurred_at: ticket.occurred_at, site_name: siteName,
          client_id: clientId, truck_plate: truckPlate.toUpperCase(),
          recipient_email: recipientEmail,
          materials: validLines.map(l => ({ material_id: l.material_id, tonnage: parseFloat(l.tonnage) })),
        },
      });

      toast.success("Pesée enregistrée");
      navigate({ to: "/ticket/$id", params: { id: ticket.id } });
    } catch (err: any) {
      toast.error(err.message || "Erreur lors de l'enregistrement");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="mx-auto max-w-3xl px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logo} alt="LocMat" className="h-10 w-auto" />
            <div>
              <h1 className="font-semibold">Bon de pesée</h1>
              <p className="text-xs text-muted-foreground">Saisie chauffeur</p>
            </div>
          </div>
          <HeaderActions />

        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-6">
        <form onSubmit={submit} className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Informations générales</CardTitle></CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Opération *</Label>
                <Select value={type} onValueChange={(v) => setType(v as any)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="depot">Dépôt</SelectItem>
                    <SelectItem value="reprise">Reprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="dt">Date et heure *</Label>
                <Input id="dt" type="datetime-local" value={occurredAt} onChange={(e) => setOccurredAt(e.target.value)} required />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="site">Nom du chantier *</Label>
                <Input id="site" value={siteName} onChange={(e) => setSiteName(e.target.value)} required />
              </div>
              <div className="space-y-1.5">
                <Label>Client *</Label>
                <Select value={clientId} onValueChange={setClientId}>
                  <SelectTrigger><SelectValue placeholder={clients.length ? "Sélectionnez" : "Aucun client en base"} /></SelectTrigger>
                  <SelectContent>
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="plate">Immatriculation *</Label>
                <Input id="plate" value={truckPlate} onChange={(e) => setTruckPlate(e.target.value)} placeholder="AB-123-CD" required />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">Matières</CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={addLine} disabled={lines.length >= maxMaterials}>
                <Plus className="h-4 w-4 mr-1" /> Ajouter
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {lines.map((line, i) => (
                <div key={i} className="grid gap-2 sm:grid-cols-[1fr_140px_auto] items-end">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Matière</Label>
                    <Select value={line.material_id} onValueChange={(v) => updateLine(i, { material_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Sélectionnez" /></SelectTrigger>
                      <SelectContent>
                        {materials.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Tonnage (t)</Label>
                    <Input type="number" step="0.001" min="0" value={line.tonnage} onChange={(e) => updateLine(i, { tonnage: e.target.value })} />
                  </div>
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeLine(i)} disabled={lines.length === 1}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Signature & contact</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label>Signature du chauffeur *</Label>
                <SignaturePad value={signature} onChange={setSignature} />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="signer">Nom du signataire *</Label>
                  <Input id="signer" value={signerName} onChange={(e) => setSignerName(e.target.value)} required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email destinataire *</Label>
                  <Input id="email" type="email" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} required />
                </div>
              </div>
            </CardContent>
          </Card>

          <Button type="submit" size="lg" className="w-full" disabled={submitting}>
            {submitting ? "Enregistrement…" : "Valider et imprimer le ticket"}
          </Button>
        </form>
      </main>
    </div>
  );
}

function HeaderActions() {
  const { isAdmin, signOut } = useAuth();
  return (
    <div className="flex items-center gap-3 text-sm">
      {isAdmin && (
        <Link to="/admin" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <Shield className="h-4 w-4" /> Admin
        </Link>
      )}
      <button onClick={signOut} className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
        <LogOut className="h-4 w-4" /> Déconnexion
      </button>
    </div>
  );
}
